import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { UserCheck, Plus, RefreshCw, Ban, ChevronRight, Target } from 'lucide-react';
import { toast } from 'sonner';
import { ScopeBuilder } from '@/features/compliance/audit/ScopeBuilder';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthStore } from '@/store/auth.store';
import {
  useAuditorTokens,
  useCreateAuditorToken,
  useRevokeAuditorToken,
  useResendAuditorInvitation,
  useAuditorTokenDetail,
} from '@/api/hooks/useAuditorTokens';
import { useFrameworks } from '@/api/hooks/useCompliance';
import type { AuditorTokenSummary, AuditorTokenStatus } from '@grc/shared';

// ── Status badge ──────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<AuditorTokenStatus, string> = {
  pending_activation: 'bg-yellow-100 text-yellow-800',
  active:             'bg-green-100 text-green-800',
  expired:            'bg-gray-100 text-gray-600',
  revoked:            'bg-red-100 text-red-700',
  lapsed_unactivated: 'bg-orange-100 text-orange-700',
};

const STATUS_LABELS: Record<AuditorTokenStatus, string> = {
  pending_activation: 'Pending Activation',
  active:             'Active',
  expired:            'Expired',
  revoked:            'Revoked',
  lapsed_unactivated: 'Lapsed',
};

function StatusBadge({ status }: { status: AuditorTokenStatus }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status]}`}>
      {STATUS_LABELS[status]}
    </span>
  );
}

// ── Create modal ──────────────────────────────────────────────────────────────

function CreateModal({ onClose }: { onClose: () => void }) {
  const { data: frameworksData } = useFrameworks();
  const create = useCreateAuditorToken();

  const [form, setForm] = useState({
    auditor_email: '',
    auditor_display_name: '',
    audit_firm_name: '',
    framework_ids: [] as string[],
    lifecycle_days: 30 as 7 | 14 | 30,
  });

  const frameworks = frameworksData ?? [];

  function toggleFramework(id: string) {
    setForm((prev) => ({
      ...prev,
      framework_ids: prev.framework_ids.includes(id)
        ? prev.framework_ids.filter((f) => f !== id)
        : [...prev.framework_ids, id],
    }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (form.framework_ids.length === 0) {
      toast.error('Select at least one framework');
      return;
    }
    try {
      await create.mutateAsync(form);
      toast.success('Auditor invitation sent');
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.errors?.[0]?.message ?? 'Failed to create invitation');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg bg-bg-surface p-6 shadow-xl">
        <h2 className="mb-4 text-lg font-semibold text-text-primary">Invite External Auditor</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary">Auditor Email *</label>
            <input
              type="email"
              required
              value={form.auditor_email}
              onChange={(e) => setForm({ ...form, auditor_email: e.target.value })}
              className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-sm text-text-primary"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary">Auditor Name *</label>
            <input
              type="text"
              required
              value={form.auditor_display_name}
              onChange={(e) => setForm({ ...form, auditor_display_name: e.target.value })}
              className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-sm text-text-primary"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary">Audit Firm *</label>
            <input
              type="text"
              required
              value={form.audit_firm_name}
              onChange={(e) => setForm({ ...form, audit_firm_name: e.target.value })}
              className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-sm text-text-primary"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary">Frameworks *</label>
            <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-border-subtle p-2">
              {frameworks.map((fw: { id: string; display_name: string }) => (
                <label key={fw.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 hover:bg-bg-surface-2">
                  <input
                    type="checkbox"
                    checked={form.framework_ids.includes(fw.id)}
                    onChange={() => toggleFramework(fw.id)}
                    className="h-4 w-4"
                  />
                  <span className="text-sm text-text-primary">{fw.display_name}</span>
                </label>
              ))}
              {frameworks.length === 0 && (
                <p className="text-xs text-text-tertiary">No active frameworks</p>
              )}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary">Access Window</label>
            <select
              value={form.lifecycle_days}
              onChange={(e) => setForm({ ...form, lifecycle_days: Number(e.target.value) as 7 | 14 | 30 })}
              className="h-9 rounded-md border border-border-subtle bg-bg-surface px-3 text-sm text-text-primary"
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Sending…' : 'Send Invitation'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Revoke modal ──────────────────────────────────────────────────────────────

/**
 * ⚠ TC5 — THE REASON IS REQUIRED, AND THE COPY SAYS WHAT REVOCATION ACTUALLY DOES.
 *
 * Revocation is immediate and history-preserving: the auditor loses access now, and the record of
 * the engagement — the granted scope, the evidence requests, what was asked and answered — stays.
 * ⓘ The reason was optional before. A revoked grant with no recorded reason cannot be reviewed
 * later, and "why did we cut that auditor off mid-audit" is exactly the question someone asks
 * months afterwards, when nobody remembers.
 */
function RevokeModal({ token, onClose }: { token: AuditorTokenSummary; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const revoke = useRevokeAuditorToken();
  const ready = reason.trim().length > 0;

  async function handleRevoke() {
    if (!ready) return;
    try {
      await revoke.mutateAsync({ id: token.id, reason: reason.trim() });
      toast.success('Auditor access revoked');
      onClose();
    } catch {
      toast.error('Failed to revoke access');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-bg-surface p-6 shadow-xl">
        <h2 className="mb-2 text-lg font-semibold text-text-primary">Revoke Auditor Access</h2>
        <p className="mb-2 text-sm text-text-secondary">
          Revoke access for <strong>{token.auditorDisplayName}</strong> ({token.auditFirmName})?
          They will be notified by email.
        </p>
        <p className="mb-4 text-sm text-text-secondary">
          {/* ⚠ Both halves said, because they are the two things people get wrong about revoking:
              that it might take effect later, and that it might erase the engagement. */}
          Access ends <strong className="text-text-primary">immediately</strong>. The engagement
          record — the granted scope and every evidence request — is{' '}
          <strong className="text-text-primary">kept</strong>.
        </p>
        <label className="mb-1 block text-xs font-medium text-text-secondary" htmlFor="revoke-reason">
          Reason for revoking
        </label>
        <textarea
          id="revoke-reason"
          aria-label="Reason for revoking"
          placeholder="Why is this access being revoked?"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          className="mb-4 w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-sm text-text-primary"
        />
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={handleRevoke} disabled={!ready || revoke.isPending}>
            {revoke.isPending ? 'Revoking…' : 'Revoke Access'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Detail drawer ─────────────────────────────────────────────────────────────

function DetailDrawer({ tokenId, onClose }: { tokenId: string; onClose: () => void }) {
  const { data, isLoading } = useAuditorTokenDetail(tokenId);

  return (
    <div className="fixed inset-y-0 right-0 z-40 w-96 border-l border-border-subtle bg-bg-surface shadow-xl overflow-y-auto">
      <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
        <h3 className="font-semibold text-text-primary">Token Detail</h3>
        <button onClick={onClose} className="text-text-tertiary hover:text-text-secondary">✕</button>
      </div>
      {isLoading ? (
        <div className="space-y-3 p-4">
          {[1,2,3].map(i => <Skeleton key={i} className="h-6 w-full" />)}
        </div>
      ) : data ? (
        <div className="space-y-4 p-4 text-sm">
          <div>
            <p className="text-xs text-text-tertiary">Auditor</p>
            <p className="font-medium text-text-primary">{data.auditorDisplayName}</p>
            <p className="text-text-secondary">{data.auditFirmName}</p>
            <p className="text-text-secondary">{data.auditorEmail}</p>
          </div>
          <div>
            <p className="text-xs text-text-tertiary">Status</p>
            <StatusBadge status={data.status} />
          </div>
          <div>
            <p className="text-xs text-text-tertiary">Frameworks</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {data.frameworkNames.map((n: string, i: number) => (
                <Badge key={i} variant="outline" className="text-xs">{n}</Badge>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-xs text-text-tertiary">Access Expires</p>
              <p className="text-text-primary">{new Date(data.expiresAt).toLocaleDateString()}</p>
            </div>
            {data.activatedAt && (
              <div>
                <p className="text-xs text-text-tertiary">Activated</p>
                <p className="text-text-primary">{new Date(data.activatedAt).toLocaleDateString()}</p>
              </div>
            )}
          </div>
          {data.revokedAt && (
            <div>
              <p className="text-xs text-text-tertiary">Revoked</p>
              <p className="text-text-primary">{new Date(data.revokedAt).toLocaleDateString()}</p>
              {data.revocationReason && (
                <p className="text-text-secondary mt-0.5">{data.revocationReason}</p>
              )}
            </div>
          )}
          <div>
            <p className="mb-1 text-xs text-text-tertiary">Recent Activity</p>
            {data.auditLogSlice.length === 0 ? (
              <p className="text-text-tertiary">No activity</p>
            ) : (
              <ul className="space-y-1">
                {data.auditLogSlice.slice(0, 10).map((row: { event_type: string; occurred_at: string }, i: number) => (
                  <li key={i} className="text-text-secondary">
                    <span className="font-mono text-xs">{row.event_type}</span>{' '}
                    <span className="text-xs text-text-tertiary">
                      {new Date(row.occurred_at).toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ── Token row ─────────────────────────────────────────────────────────────────

function TokenRow({
  token,
  canManage,
  onRevoke,
  onResend,
  onDetail,
  onScope,
}: {
  token: AuditorTokenSummary;
  canManage: boolean;
  onRevoke: (t: AuditorTokenSummary) => void;
  onResend: (id: string) => void;
  onDetail: (id: string) => void;
  onScope: (t: AuditorTokenSummary) => void;
}) {
  return (
    <tr className="border-b border-border-subtle hover:bg-bg-surface-2">
      <td className="px-4 py-3">
        <div className="font-medium text-text-primary">{token.auditorDisplayName}</div>
        <div className="text-xs text-text-tertiary">{token.auditorEmail}</div>
      </td>
      <td className="px-4 py-3 text-sm text-text-secondary">{token.auditFirmName}</td>
      <td className="px-4 py-3">
        <StatusBadge status={token.status} />
      </td>
      <td className="px-4 py-3 text-sm text-text-secondary">
        {token.frameworkNames.join(', ') || '—'}
      </td>
      <td className="px-4 py-3 text-sm text-text-secondary">
        {new Date(token.expiresAt).toLocaleDateString()}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {canManage && token.status === 'pending_activation' && (
            <button
              onClick={() => onResend(token.id)}
              title="Resend invitation"
              className="text-text-tertiary hover:text-text-primary"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          )}
          {/* ⚠ TC5 — the scope BUILDER lives here, beside the grant it belongs to. What it stores
              is the RESOLVED control set: the frameworks column above records how the scope was
              built, not what the auditor may see. */}
          {canManage && (token.status === 'active' || token.status === 'pending_activation') && (
            <button
              onClick={() => onScope(token)}
              title="Set audit scope"
              className="text-text-tertiary hover:text-accent-cyan"
            >
              <Target className="h-4 w-4" />
            </button>
          )}
          {canManage && (token.status === 'active' || token.status === 'pending_activation') && (
            <button
              onClick={() => onRevoke(token)}
              title="Revoke access"
              className="text-text-tertiary hover:text-red-500"
            >
              <Ban className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => onDetail(token.id)}
            title="View detail"
            className="text-text-tertiary hover:text-text-primary"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function AuditorAccessPage() {
  const { hasAnyRole } = useAuthStore();
  const canManage = hasAnyRole(['super_admin', 'risk_manager']);

  if (!canManage) return <Navigate to="/settings" replace />;

  const [showCreate, setShowCreate] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<AuditorTokenSummary | null>(null);
  const [scopeTarget, setScopeTarget] = useState<AuditorTokenSummary | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<AuditorTokenStatus | undefined>(undefined);

  const { data, isLoading } = useAuditorTokens(statusFilter ? { status: statusFilter } : undefined);
  const resend = useResendAuditorInvitation();
  const tokens = data?.data ?? [];

  async function handleResend(id: string) {
    try {
      await resend.mutateAsync(id);
      toast.success('Invitation resent');
    } catch (err: any) {
      toast.error(err?.response?.data?.errors?.[0]?.message ?? 'Failed to resend invitation');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserCheck className="h-5 w-5 text-text-secondary" />
          <h1 className="text-xl font-semibold text-text-primary">Auditor Access</h1>
        </div>
        {canManage && (
          <Button onClick={() => setShowCreate(true)} size="sm">
            <Plus className="mr-1 h-4 w-4" /> Invite Auditor
          </Button>
        )}
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between py-3">
          <CardTitle className="text-sm font-medium">Access Tokens</CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-xs text-text-tertiary">Filter:</span>
            <select
              value={statusFilter ?? ''}
              onChange={(e) => setStatusFilter((e.target.value as AuditorTokenStatus) || undefined)}
              className="h-7 rounded border border-border-subtle bg-bg-surface px-2 text-xs text-text-primary"
            >
              <option value="">All statuses</option>
              <option value="pending_activation">Pending Activation</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="revoked">Revoked</option>
              <option value="lapsed_unactivated">Lapsed</option>
            </select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-3 p-4">
              {[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : tokens.length === 0 ? (
            <div className="py-12 text-center text-text-tertiary">
              <UserCheck className="mx-auto mb-2 h-8 w-8 opacity-40" />
              <p className="text-sm">No auditor tokens found</p>
              {canManage && (
                <Button size="sm" variant="outline" className="mt-3" onClick={() => setShowCreate(true)}>
                  Invite your first auditor
                </Button>
              )}
            </div>
          ) : (
            <table className="w-full">
              <thead className="border-b border-border-subtle">
                <tr className="text-left text-xs text-text-tertiary">
                  <th className="px-4 py-2 font-medium">Auditor</th>
                  <th className="px-4 py-2 font-medium">Firm</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Frameworks</th>
                  <th className="px-4 py-2 font-medium">Expires</th>
                  <th className="px-4 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((token) => (
                  <TokenRow
                    key={token.id}
                    token={token}
                    canManage={canManage}
                    onRevoke={setRevokeTarget}
                    onScope={setScopeTarget}
                    onResend={handleResend}
                    onDetail={setDetailId}
                  />
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} />}
      {revokeTarget && <RevokeModal token={revokeTarget} onClose={() => setRevokeTarget(null)} />}
      {detailId && <DetailDrawer tokenId={detailId} onClose={() => setDetailId(null)} />}
      {scopeTarget && (
        <ScopeBuilder
          tokenId={scopeTarget.id}
          auditorName={scopeTarget.auditorDisplayName}
          onClose={() => setScopeTarget(null)}
        />
      )}
    </div>
  );
}
